// Design System DataTable Component
// Unified data table with sorting, selection, and actions

import React, { useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Checkbox,
  IconButton,
  Box,
  Tooltip,
  Toolbar,
  Typography,
  useTheme,
  styled,
  alpha,
  useMediaQuery,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';
import { borderTokens, shadowTokens, spacingTokens, transitionTokens } from '../../tokens';
import Skeleton from '../core/Skeleton';

// Styled components
const StyledTableContainer = styled(Paper)(({ theme, variant }) => ({
  borderRadius: borderTokens.lg,
  boxShadow: variant === 'elevated' ? shadowTokens.medium : 'none',
  border: variant === 'outlined' ? `1px solid ${theme.palette.divider}` : 'none',
  overflow: 'hidden',
}));

const StyledTableHead = styled(TableHead)(({ theme, stickyHeader }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    ...(stickyHeader && {
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, clickable }) => ({
  cursor: clickable ? 'pointer' : 'default',
  transition: transitionTokens.smooth,
  
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
  
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme, dense }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  ...(dense && {
    minHeight: 48,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  }),
}));

// Enhanced table header with selection
const EnhancedTableHead = ({
  columns,
  order,
  orderBy,
  onRequestSort,
  onSelectAllClick,
  numSelected,
  rowCount,
  selectable,
  showActions,
}) => {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <StyledTableHead stickyHeader>
      <TableRow>
        {selectable && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'select all items',
              }}
            />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align || 'left'}
            padding={column.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === column.id ? order : false}
            sx={{ width: column.width, minWidth: column.minWidth }}
          >
            {column.sortable !== false ? (
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : 'asc'}
                onClick={createSortHandler(column.id)}
              >
                {column.label}
                {orderBy === column.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              column.label
            )}
          </TableCell>
        ))}
        {showActions && (
          <TableCell align="right" sx={{ width: 100 }}>
            Actions
          </TableCell>
        )}
      </TableRow>
    </StyledTableHead>
  );
};

// Enhanced table toolbar
const EnhancedTableToolbar = ({ numSelected, title, actions, selectedActions }) => {
  const theme = useTheme();

  return (
    <StyledToolbar
      dense
      sx={{
        ...(numSelected > 0 && {
          bgcolor: alpha(theme.palette.primary.main, 0.08),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {title}
        </Typography>
      )}

      {numSelected > 0 ? selectedActions : actions}
    </StyledToolbar>
  );
};

/**
 * DataTable - Unified data table component
 * 
 * Features:
 * - Sorting by columns
 * - Row selection (single/multiple)
 * - Pagination
 * - Custom cell rendering
 * - Row actions
 * - Loading states
 * - Empty states
 * - Responsive design
 * - Sticky header option
 * 
 * @example
 * // Basic usage
 * <DataTable
 *   rows={data}
 *   columns={[
 *     { id: 'name', label: 'Name', sortable: true },
 *     { id: 'email', label: 'Email' },
 *     { id: 'status', label: 'Status', renderCell: (row) => <StatusBadge status={row.status} /> }
 *   ]}
 * />
 * 
 * // With selection and actions
 * <DataTable
 *   rows={users}
 *   columns={columns}
 *   selectable
 *   onSelectionChange={setSelectedUsers}
 *   rowActions={(row) => (
 *     <>
 *       <IconButton size="small" onClick={() => handleEdit(row)}>
 *         <EditIcon />
 *       </IconButton>
 *       <IconButton size="small" onClick={() => handleDelete(row)}>
 *         <DeleteIcon />
 *       </IconButton>
 *     </>
 *   )}
 * />
 */
const DataTable = ({
  // Data
  rows = [],
  columns = [],
  getRowId = (row) => row.id || row._id,
  
  // Features
  selectable = false,
  selected = [],
  onSelectionChange,
  onRowClick,
  
  // Sorting
  defaultOrderBy,
  defaultOrder = 'asc',
  onSort,
  
  // Pagination
  paginated = true,
  rowsPerPageOptions = [5, 10, 25, 50],
  defaultRowsPerPage = 10,
  
  // Display
  title,
  variant = 'outlined',
  size = 'medium',
  stickyHeader = false,
  showToolbar = true,
  
  // Actions
  actions,
  selectedActions,
  rowActions,
  
  // States
  loading = false,
  emptyMessage = 'No data available',
  emptyIcon,
  
  // Style
  sx,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('md') || '(max-width: 960px)');
  
  // State
  const [order, setOrder] = React.useState(defaultOrder);
  const [orderBy, setOrderBy] = React.useState(defaultOrderBy || columns[0]?.id);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [internalSelected, setInternalSelected] = React.useState(selected);
  
  // Use external selected state if provided
  const selectedRows = onSelectionChange ? selected : internalSelected;
  const setSelectedRows = onSelectionChange || setInternalSelected;
  
  // Sorting logic
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    
    if (onSort) {
      onSort(property, newOrder);
    }
  };
  
  // Selection logic
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((row) => getRowId(row));
      setSelectedRows(newSelected);
      return;
    }
    setSelectedRows([]);
  };
  
  const handleClick = (event, row) => {
    const id = getRowId(row);
    
    if (selectable && (event.target.type === 'checkbox' || event.ctrlKey || event.metaKey)) {
      const selectedIndex = selectedRows.indexOf(id);
      let newSelected = [];
      
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRows, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRows.slice(1));
      } else if (selectedIndex === selectedRows.length - 1) {
        newSelected = newSelected.concat(selectedRows.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRows.slice(0, selectedIndex),
          selectedRows.slice(selectedIndex + 1),
        );
      }
      
      setSelectedRows(newSelected);
    } else if (onRowClick) {
      onRowClick(row);
    }
  };
  
  const isSelected = (id) => selectedRows.indexOf(id) !== -1;
  
  // Pagination logic
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Sort rows
  const sortedRows = useMemo(() => {
    if (!orderBy || onSort) return rows; // If external sort, don't sort internally
    
    return [...rows].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (bValue < aValue) {
        return order === 'asc' ? 1 : -1;
      }
      if (bValue > aValue) {
        return order === 'asc' ? -1 : 1;
      }
      return 0;
    });
  }, [rows, order, orderBy, onSort]);
  
  // Paginate rows
  const displayedRows = paginated
    ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedRows;
  
  // Responsive columns
  const visibleColumns = useMemo(() => {
    if (!isMobile) return columns;
    
    // On mobile, show only priority columns
    return columns.filter(col => col.priority !== 'low');
  }, [columns, isMobile]);
  
  // Loading skeleton
  if (loading) {
    return (
      <StyledTableContainer variant={variant} sx={sx}>
        <Table size={size}>
          <StyledTableHead>
            <TableRow>
              {selectable && <TableCell padding="checkbox"><Skeleton variant="rectangular" width={24} height={24} /></TableCell>}
              {visibleColumns.map((column) => (
                <TableCell key={column.id}>
                  <Skeleton variant="text" />
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                {selectable && <TableCell padding="checkbox"><Skeleton variant="rectangular" width={24} height={24} /></TableCell>}
                {visibleColumns.map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    );
  }
  
  // Empty state
  if (rows.length === 0) {
    return (
      <StyledTableContainer variant={variant} sx={sx}>
        <Box
          sx={{
            py: 8,
            px: 2,
            textAlign: 'center',
          }}
        >
          {emptyIcon && <Box sx={{ mb: 2 }}>{emptyIcon}</Box>}
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      </StyledTableContainer>
    );
  }
  
  return (
    <StyledTableContainer variant={variant} sx={sx} {...props}>
      {showToolbar && (title || actions || selectedActions) && (
        <EnhancedTableToolbar
          numSelected={selectedRows.length}
          title={title}
          actions={actions}
          selectedActions={selectedActions}
        />
      )}
      
      <TableContainer sx={{ maxHeight: stickyHeader ? 440 : undefined }}>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={size}
          stickyHeader={stickyHeader}
        >
          <EnhancedTableHead
            columns={visibleColumns}
            numSelected={selectedRows.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            selectable={selectable}
            showActions={!!rowActions}
          />
          
          <TableBody>
            {displayedRows.map((row, index) => {
              const isItemSelected = isSelected(getRowId(row));
              const labelId = `enhanced-table-checkbox-${index}`;
              
              return (
                <StyledTableRow
                  hover
                  onClick={(event) => handleClick(event, row)}
                  role={selectable ? 'checkbox' : undefined}
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={getRowId(row)}
                  selected={isItemSelected}
                  clickable={!!onRowClick}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                  )}
                  
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      padding={column.disablePadding ? 'none' : 'normal'}
                    >
                      {column.renderCell 
                        ? column.renderCell(row, column)
                        : row[column.id]
                      }
                    </TableCell>
                  ))}
                  
                  {rowActions && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {rowActions(row)}
                      </Box>
                    </TableCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {paginated && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </StyledTableContainer>
  );
};

DataTable.propTypes = {
  // Data
  rows: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    align: PropTypes.oneOf(['left', 'center', 'right', 'justify']),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sortable: PropTypes.bool,
    priority: PropTypes.oneOf(['high', 'medium', 'low']),
    disablePadding: PropTypes.bool,
    renderCell: PropTypes.func,
  })).isRequired,
  getRowId: PropTypes.func,
  
  // Features
  selectable: PropTypes.bool,
  selected: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onRowClick: PropTypes.func,
  
  // Sorting
  defaultOrderBy: PropTypes.string,
  defaultOrder: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  
  // Pagination
  paginated: PropTypes.bool,
  rowsPerPageOptions: PropTypes.array,
  defaultRowsPerPage: PropTypes.number,
  
  // Display
  title: PropTypes.node,
  variant: PropTypes.oneOf(['outlined', 'elevated', 'plain']),
  size: PropTypes.oneOf(['small', 'medium']),
  stickyHeader: PropTypes.bool,
  showToolbar: PropTypes.bool,
  
  // Actions
  actions: PropTypes.node,
  selectedActions: PropTypes.node,
  rowActions: PropTypes.func,
  
  // States
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  emptyIcon: PropTypes.node,
  
  sx: PropTypes.object,
};

export default DataTable;